<template>
	<div
		id="loreView"
		:class="{ animate: animateView }"
		:style="{ 'animation-delay': animationDelay }"
		class="content-container"
	>
		<section id="lore-list" :class="{ animate: animate }" class="section-container">
			<div class="section-header clipped-medium-backward">
				<img src="/icons/open-book.svg" />
				<h1>THE GRAND ARCHIVE</h1>
			</div>
			<div class="section-content-container lore-fs-panel">
				<div class="lore-search">
					<input
						v-model="search"
						type="text"
						class="search-input"
						placeholder="Search the archive..."
					/>
					<span v-if="search" class="lore-search-count">{{ searchResults.length }} found</span>
				</div>

				<!-- Search results replace the tree while a query is typed -->
				<ul v-if="search" class="lore-fs-list">
					<li
						v-for="hit in searchResults"
						:key="hit.file"
						class="lore-file lore-search-hit"
						:class="{ 'lore-file-active': isActive(hit.file) }"
						@click="openFile(hit.file)"
					>
						<span class="lore-hit-title">{{ hit.name }}</span>
						<span class="lore-hit-path">{{ hit.path }}</span>
					</li>
					<li v-if="!searchResults.length" class="lore-file lore-file-locked">
						— no records match —
					</li>
				</ul>

				<ul v-else ref="tree" class="lore-fs-list">
					<li
						v-for="folder in filesystem.folders"
						:key="folder.name"
						class="lore-folder"
						:data-key="folder.name"
					>
						<span class="lore-folder-name" @click="toggle(folder.name)">
							<span class="lore-caret">{{ isOpen(folder.name) ? "▼" : "▶" }}</span>
							{{ folderLabel(folder.name) }}
						</span>
						<ul v-if="isOpen(folder.name)">
							<li
								v-for="file in folder.files || []"
								:key="file.file"
								class="lore-file"
								:class="{
									'lore-file-locked': !recordFor(file.file),
									'lore-file-active': isActive(file.file),
								}"
								@click="openFile(file.file)"
							>
								{{ file.name }}
							</li>
							<li
								v-for="sub in folder.folders || []"
								:key="sub.name"
								class="lore-subfolder-item"
								:data-key="folder.name + '/' + sub.name"
							>
								<span class="lore-subfolder-name" @click="toggle(folder.name + '/' + sub.name)">
									<span class="lore-caret">{{
										isOpen(folder.name + "/" + sub.name) ? "▼" : "▶"
									}}</span>
									{{ sub.name }}
								</span>
								<ul v-if="isOpen(folder.name + '/' + sub.name)">
									<li
										v-for="file in sub.files"
										:key="file.file"
										class="lore-file"
										:class="{
											'lore-file-locked': !recordFor(file.file),
											'lore-file-active': isActive(file.file),
										}"
										@click="openFile(file.file)"
									>
										{{ file.name }}
									</li>
									<li
										v-if="!sub.files || sub.files.length === 0"
										class="lore-file lore-file-locked"
									>
										— empty —
									</li>
								</ul>
							</li>
						</ul>
					</li>
				</ul>
			</div>
		</section>

		<div class="section-gap">
			<div class="source-tag" v-decrypt="{ after: '#loreView', delay: 0.1 }">
				// Records retrieved from House of Remembrance's Grand Archive //
			</div>
		</div>

		<section id="lore-detail" :class="{ animate: animate }" class="section-container">
			<div style="height: 52px; overflow: hidden">
				<div class="section-header clipped-medium-backward-events-logs">
					<img src="/icons/conversation.svg" />
					<h1>RECORD ENTRY</h1>
				</div>
				<div class="rhombus-back">&nbsp;</div>
			</div>
			<div class="section-content-container extra-margins">
				<div v-if="loading" class="lore-loading">
					<span class="lore-loading-label">// RETRIEVING RECORD</span>
					<span class="lore-loading-name">{{ loading }}</span>
					<div class="lore-loading-track"><div ref="loadBar" class="lore-loading-bar"></div></div>
				</div>
				<article
					class="lore-record"
					v-else-if="selectedRecord && selectedRecord.title"
					ref="record"
				>
					<div class="lore-record-head">
						<div class="lore-breadcrumb" v-if="currentEntry">{{ currentEntry.path }}</div>
						<div class="lore-record-title">{{ selectedRecord.title }}</div>
						<div v-if="recordSubtitle" class="lore-record-meta" v-html="recordSubtitle"></div>
					</div>
					<vue-markdown-it
						:source="selectedRecord.content"
						:plugins="mdPlugins"
						class="lore-body"
					/>
				</article>
				<div class="detail-empty" v-else>
					<p>// SELECT AN ENTRY TO VIEW RECORD //</p>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
import { VueMarkdownIt } from "@f3ve/vue-markdown-it";
import markdownItFootnote from "markdown-it-footnote";
import { retrieveRows, revealRecord, runLoadBar, reducedMotion } from "@/utils/retrieve";
import { onEscape } from "@/utils/escape";
import filesystemData from "@/data/lore/lore-filesystem.json";

export default {
	components: {
		VueMarkdownIt,
	},
	props: {
		animate: {
			type: Boolean,
			required: true,
		},
		records: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			animateView: this.animate,
			animationDelay: "1.75s",
			selectedRecord: null,
			filesystem: filesystemData,
			// Folder keys ("Top" or "Top/Sub") that are expanded
			openFolders: {}, // everything starts closed
			search: "",
			loading: null, // name of the record being "retrieved"
			openToken: 0,
			mdPlugins: [markdownItFootnote],
		};
	},
	computed: {
		// Every entry in reading order, with its folder path
		entries() {
			const list = [];
			this.filesystem.folders.forEach(folder => {
				(folder.files || []).forEach(f =>
					list.push({ ...f, path: folder.name, keys: [folder.name] })
				);
				(folder.folders || []).forEach(sub =>
					(sub.files || []).forEach(f =>
						list.push({
							...f,
							path: `${folder.name} / ${sub.name}`,
							keys: [folder.name, `${folder.name}/${sub.name}`],
						})
					)
				);
			});
			return list;
		},
		currentEntry() {
			return this.selectedRecord
				? this.entries.find(e => e.file === this.selectedRecord.filename)
				: null;
		},
		searchResults() {
			const q = this.search.trim().toLowerCase();
			if (!q) return [];
			const inTitle = [],
				inText = [];
			this.entries.forEach(e => {
				const record = this.recordFor(e.file);
				if (!record) return;
				if (e.name.toLowerCase().includes(q)) inTitle.push(e);
				else if (record.content.toLowerCase().includes(q)) inText.push(e);
			});
			return [...inTitle, ...inText];
		},
		recordSubtitle() {
			if (!this.selectedRecord) return "";
			const loc = this.selectedRecord.location?.trim();
			const time = this.selectedRecord.time?.trim();
			if (loc && time) return `${loc} // ${time}`;
			if (time) return time;
			if (loc) return loc;
			return "";
		},
	},
	beforeUnmount() {
		this.offEscape?.();
	},
	mounted() {
		// Esc: close the open record, then clear the search
		this.offEscape = onEscape(() => {
			if (this.selectedRecord || this.loading) {
				this.openToken++;
				this.loading = null;
				this.selectedRecord = null;
				return true;
			}
			if (this.search) return (this.search = ""), true;
			return false;
		});
		if (this.animate && this.$refs.tree) {
			const rows = this.$refs.tree.querySelectorAll(
				".lore-folder-name, .lore-subfolder-name, .lore-file"
			);
			retrieveRows(rows, { startDelay: 0.7, gap: 0.06 });
		}
	},
	methods: {
		recordFor(filename) {
			return this.records.find(r => r.filename === filename) || null;
		},
		selectRecord(record) {
			this.selectedRecord = record;
		},
		openFile(file) {
			const record = this.recordFor(file);
			if (!record) return;
			const entry = this.entries.find(e => e.file === file);
			if (entry) entry.keys.forEach(k => (this.openFolders[k] = true));
			const token = ++this.openToken;
			const show = () => {
				if (token !== this.openToken) return;
				this.loading = null;
				this.selectedRecord = record;
				this.$nextTick(() => {
					const article = this.$refs.record;
					if (!article) return;
					const box = article.closest(".section-content-container");
					if (box) box.scrollTop = 0;
					const body = article.querySelector(".lore-body");
					revealRecord(article.querySelector(".lore-record-head"), body ? body.children : []);
				});
			};
			// A short retrieval, then the record reveals itself
			if (reducedMotion()) return show();
			this.loading = entry ? entry.name : record.title;
			this.$nextTick(() => runLoadBar(this.$refs.loadBar, 0.55));
			setTimeout(show, 600);
		},
		isActive(file) {
			return !!this.selectedRecord && this.selectedRecord.filename === file;
		},
		isOpen(key) {
			return !!this.openFolders[key];
		},
		toggle(key) {
			this.openFolders[key] = !this.openFolders[key];
			// Opening a folder retrieves its contents row by row
			if (!this.openFolders[key]) return;
			this.$nextTick(() => {
				const li =
					this.$refs.tree &&
					[...this.$refs.tree.querySelectorAll("[data-key]")].find(el => el.dataset.key === key);
				const list = li && li.querySelector(":scope > ul");
				if (list)
					retrieveRows(
						list.querySelectorAll(":scope > li > .lore-subfolder-name, :scope > li.lore-file"),
						{ gap: 0.11, duration: 0.6 }
					);
			});
		},
		folderLabel(name) {
			// Strip leading "/ " or "/" prefix, then uppercase
			return name.replace(/^\/\s*/, "").toUpperCase();
		},
	},
};
</script>

<style scoped>
section.section-container#lore-detail .rhombus-back {
	top: -2em;
	left: -2em;
}

/* Footnotes */
:deep(hr.footnotes-sep) {
	border: none;
	border-top: 1px solid rgba(122, 28, 46, 0.4);
	margin: 2em 0 1em;
}

:deep(.footnotes) {
	font-size: 12px;
	color: rgba(209, 200, 184, 0.6);
}

:deep(.footnotes-list) {
	padding-left: 1.5em;
	margin: 0;
}

:deep(.footnote-item) {
	margin-bottom: 0.4em;
	line-height: 1.6;
}

:deep(.footnote-item p) {
	margin: 0;
}

:deep(.footnote-ref) {
	font-size: 0.6em;
	vertical-align: super;
	line-height: 0;
}

:deep(.footnote-ref a) {
	font-size: inherit;
}

:deep(.footnote-ref a) {
	color: #b8973a;
	text-decoration: none;
}

:deep(.footnote-ref a:hover) {
	text-decoration: underline;
}

:deep(.footnote-backref) {
	display: none;
}

/* Filesystem panel */
.lore-fs-panel {
	padding: 6px 0 !important;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.lore-fs-list {
	list-style: none;
	padding: 0;
	margin: 0;
	flex: 1;
	overflow-y: auto;
}

/* Folder */
.lore-folder {
	margin-bottom: 2px;
}

.lore-folder-name {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 7px 14px 5px;
	font-family: "Big Shoulders Display", cursive;
	font-size: 11px;
	font-weight: 800;
	letter-spacing: 0.18em;
	color: rgba(184, 151, 58, 0.85);
	cursor: pointer;
	user-select: none;
}

.lore-folder-name:hover {
	color: #b8973a;
}

.lore-folder > ul {
	list-style: none;
	padding: 0 0 8px 0;
	margin: 0;
}

/* File entries */
.lore-file {
	padding: 5px 14px 5px 26px;
	font-family: "Titillium Web", sans-serif;
	font-size: 13px;
	color: #d1c8b8;
	cursor: pointer;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.lore-file:hover {
	color: #fff;
	background-color: rgba(122, 28, 46, 0.35);
}

.lore-file-active {
	color: #fff;
	background-color: rgba(122, 28, 46, 0.5);
}

.lore-file-locked {
	color: rgba(209, 200, 184, 0.25);
	cursor: default;
}

.lore-file-locked:hover {
	color: rgba(209, 200, 184, 0.3);
	background-color: transparent;
}

/* Subfolder */
.lore-subfolder-item {
	list-style: none;
	padding: 0;
}

.lore-subfolder-name {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 5px 14px 5px 26px;
	font-family: "Titillium Web", sans-serif;
	font-size: 13px;
	color: #d1c8b8;
	cursor: pointer;
	user-select: none;
}

.lore-subfolder-name:hover {
	color: #fff;
	background-color: rgba(122, 28, 46, 0.2);
}

.lore-caret {
	font-size: 9px;
	color: rgba(184, 151, 58, 0.6);
}

.lore-subfolder-item > ul {
	list-style: none;
	padding: 0 0 4px 0;
	margin: 0;
}

.lore-subfolder-item > ul .lore-file {
	padding-left: 42px;
}

/* Search */
.lore-search {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 6px 14px 10px;
	border-bottom: 1px solid rgba(122, 28, 46, 0.4);
	margin-bottom: 4px;
}

.lore-search .search-input {
	flex: 1;
	min-width: 0;
}

.lore-search-count {
	flex-shrink: 0;
	font-family: "Share Tech Mono", monospace;
	font-size: 10px;
	color: rgba(209, 200, 184, 0.5);
}

.lore-search-hit {
	display: flex;
	flex-direction: column;
	padding-left: 14px;
	white-space: normal;
}

.lore-hit-path {
	font-family: "Share Tech Mono", monospace;
	font-size: 10px;
	letter-spacing: 0.05em;
	color: rgba(184, 151, 58, 0.6);
	text-transform: uppercase;
}

/* ── Record retrieval ── */
.lore-loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.6em;
	height: 100%;
	min-height: 300px;
}

.lore-loading-label {
	font-family: "Share Tech Mono", monospace;
	font-size: 11px;
	letter-spacing: 0.2em;
	color: rgba(184, 151, 58, 0.8);
}

.lore-loading-name {
	font-family: "Big Shoulders Display", cursive;
	font-size: 22px;
	font-weight: 800;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--text-mission-status);
}

.lore-loading-track {
	width: 260px;
	height: 2px;
	background: rgba(209, 200, 184, 0.12);
	overflow: hidden;
}

.lore-loading-bar {
	height: 100%;
	background: #b8973a;
	box-shadow: 0 0 8px rgba(184, 151, 58, 0.7);
	transform-origin: left center;
	transform: scaleX(0);
}

/* ── Record view ── */
.lore-record {
	padding: 1.5em 2em 2.5em;
}

.lore-record-head {
	padding-bottom: 1em;
	margin-bottom: 1.6em;
	border-bottom: 1px solid rgba(122, 28, 46, 0.6);
}

.lore-breadcrumb {
	margin-bottom: 0.5em;
	font-family: "Share Tech Mono", monospace;
	font-size: 10px;
	letter-spacing: 0.15em;
	text-transform: uppercase;
	color: rgba(209, 200, 184, 0.45);
}

.lore-record-title {
	margin: 0;
	font-family: "Big Shoulders Display", cursive;
	font-size: 34px;
	font-weight: 800;
	line-height: 1.05;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: var(--text-mission-status);
}

.lore-record-meta {
	margin-top: 0.45em;
	font-family: "Share Tech Mono", monospace;
	font-size: 12px;
	letter-spacing: 0.15em;
	text-transform: uppercase;
	color: #b8973a;
}

.lore-record-meta :deep(small) {
	font-size: 0.85em;
	letter-spacing: 0.1em;
}

/* Body typography: one scale, one rhythm */
.lore-body {
	font-family: "Titillium Web", sans-serif;
	font-size: 15px;
	line-height: 1.7;
	color: var(--text-markdown-p);
}

.lore-body > :deep(:first-child) {
	margin-top: 0;
}

.lore-body :deep(p) {
	margin: 0 0 1em;
}

.lore-body :deep(h2),
.lore-body :deep(h3),
.lore-body :deep(h4),
.lore-body :deep(h5) {
	padding: 0;
	align-self: auto;
	font-weight: 800;
	text-transform: uppercase;
}

.lore-body :deep(h2) {
	margin: 2em 0 0.8em;
	padding-bottom: 0.3em;
	border-bottom: 1px solid rgba(184, 151, 58, 0.3);
	font-family: "Big Shoulders Display", cursive;
	font-size: 22px;
	letter-spacing: 0.1em;
	color: #b8973a;
}

.lore-body :deep(h3) {
	margin: 1.6em 0 0.5em;
	font-family: "Big Shoulders Display", cursive;
	font-size: 17px;
	letter-spacing: 0.1em;
	color: var(--text-mission-status);
}

.lore-body :deep(h4),
.lore-body :deep(h5) {
	margin: 1.3em 0 0.4em;
	font-family: "Titillium Web", sans-serif;
	font-size: 13px;
	letter-spacing: 0.12em;
	color: rgba(209, 200, 184, 0.75);
}

.lore-body :deep(h2 + h3),
.lore-body :deep(h3 + h4) {
	margin-top: 0.6em;
}

.lore-body :deep(strong) {
	font-weight: 600;
	color: #e6dfd3;
}

.lore-body :deep(ul),
.lore-body :deep(ol) {
	margin: 0 0 1em;
	padding-left: 1.4em;
}

.lore-body :deep(ul) {
	list-style: square;
}

.lore-body :deep(li) {
	margin-bottom: 0.4em;
}

.lore-body :deep(li::marker) {
	color: #b8973a;
}

.lore-body :deep(blockquote) {
	margin: 0 0 1em;
	padding: 0.2em 0 0.2em 1em;
	border-left: 2px solid #b8973a;
	font-style: italic;
	color: rgba(209, 200, 184, 0.7);
}

.lore-body :deep(blockquote p:last-child) {
	margin-bottom: 0;
}

.lore-body :deep(a) {
	color: #b8973a;
	text-decoration: none;
}

.lore-body :deep(a:hover) {
	text-decoration: underline;
}

/* Wiki credit line at the end of each record */
.lore-body :deep(hr) {
	margin: 2.5em 0 0.8em;
	border: 0;
	border-top: 1px solid rgba(122, 28, 46, 0.6);
}

.lore-body :deep(hr + p) {
	margin: 0;
	font-size: 11px;
	line-height: 1.5;
	color: rgba(209, 200, 184, 0.45);
}

.lore-body :deep(hr + p em) {
	font-style: normal;
}
</style>
